import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Send, ArrowLeftRight } from "lucide-react";
import { NeuButton, NeuInput } from "../../components/neu/Neu";
import { NeuDropdown } from "../../components/data/NeuDropdown";
import { createTransfer, listBalances, listWarehouses } from "../../api/warehouse";
import { TransferLines } from "./TransferLines";
import { localName } from "../../lib/localName";

// شاشة المناقلة (شاشة كاملة): ترويسة بنفسجية ثابتة أعلى الشاشة + جسم قابل للتمرير.
export const TransferForm = ({ branchId, onClose, onDone }) => {
  const { t } = useTranslation();
  const [ws, setWs] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [available, setAvailable] = useState([]);
  const [lines, setLines] = useState([]);

  useEffect(() => { listWarehouses(branchId).then((d) => setWs(d.items || [])); }, [branchId]);
  useEffect(() => {
    if (!from) { setAvailable([]); return; }
    listBalances({ warehouse_id: from }).then((d) =>
      setAvailable((d.items || []).filter((r) => (r.qty || 0) > 0))).catch(() => setAvailable([]));
  }, [from]);

  const addLine = (row) => { if (lines.find((l) => l.item_id === row.item_id)) return;
    setLines([...lines, { item_id: row.item_id, item_code: row.item_code, item_name: row.item_name,
      uom: row.uom, qty: 1, unit_cost: row.avg_cost || 0, max: row.qty }]); };

  const save = async () => {
    try {
      await createTransfer({ branch_id: branchId, from_warehouse_id: from, to_warehouse_id: to,
        date: new Date().toISOString().slice(0, 10), reason: reason || null,
        lines: lines.map(({ max, ...l }) => l) });
      toast.success(t("warehouse.tr.notified")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail?.message_ar
                                || e?.response?.data?.detail?.code || t("errors.generic")); }
  };
  const canSave = from && to && from !== to && lines.length > 0;
  const opt = (w) => ({ value: w.warehouse_id,
    label: `${localName(w)} — ${w.keeper_name || t("warehouse.tr.noKeeper")}` });

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-background" data-testid="transfer-form">
      <div className="flex flex-shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-violet-600 to-purple-600 px-6 pb-4 pt-[max(1rem,calc(env(safe-area-inset-top)+0.75rem))] text-white shadow-xl">
        <div className="flex items-center gap-3">
          <ArrowLeftRight className="h-6 w-6" />
          <div><p className="text-lg font-black sm:text-xl">{t("warehouse.tr.title")}</p>
            <p className="hidden text-[11px] opacity-80 sm:block">{t("warehouse.tr.flowHint")}</p></div>
        </div>
        <button onClick={onClose} data-testid="trf-close"
                className="rounded-full bg-white/15 p-2 transition hover:bg-white/25">
          <X className="h-6 w-6" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
        <div className="mx-auto grid max-w-7xl gap-5 lg:grid-cols-2">
          <div className="grid gap-4">
            <div><p className="label-xs mb-2">{t("warehouse.tr.fromLabel")} *</p>
              <NeuDropdown testid="trf-from" value={from} onChange={setFrom}
                           placeholder={t("warehouse.tr.fromPlaceholder")} options={ws.map(opt)} /></div>
            <div><p className="label-xs mb-2">{t("warehouse.tr.itemsLabel")}</p>
              {!from ? (<div className="rounded-2xl bg-amber-500/10 p-6 text-center text-xs text-amber-700">
                {t("warehouse.tr.pickSourceFirst")}</div>) : available.length === 0 ? (
                <div className="rounded-2xl bg-rose-500/10 p-6 text-center text-xs text-rose-700">
                  {t("warehouse.tr.noStock")}</div>) : (
                <div className="grid max-h-96 gap-1 overflow-y-auto rounded-2xl bg-primary/5 p-2">
                  {available.map((row) => (
                    <button key={row.item_id} data-testid={`trf-avail-${row.item_id}`}
                            className="flex items-center justify-between rounded-xl p-2 text-xs hover:bg-violet-500/10"
                            onClick={() => addLine(row)}>
                      <span className="font-bold">+ {row.item_name}</span>
                      <span className="num font-mono text-[10px]">{row.qty} {row.uom}</span>
                    </button>))}</div>)}</div>
          </div>
          <div className="grid gap-4">
            <div><p className="label-xs mb-2">{t("warehouse.tr.toLabel")} *</p>
              <NeuDropdown testid="trf-to" value={to} onChange={setTo}
                           placeholder={t("warehouse.tr.toPlaceholder")}
                           options={ws.filter((w) => w.warehouse_id !== from).map(opt)} /></div>
            <div><p className="label-xs mb-2">{t("warehouse.tr.selected")} ({lines.length})</p>
              <div className="min-h-[160px] rounded-2xl bg-violet-500/5 p-3">
                {lines.length ? <TransferLines lines={lines} setLines={setLines} />
                  : <p className="p-8 text-center text-xs text-muted-foreground">{t("warehouse.tr.noSelected")}</p>}</div></div>
          </div>
          <div className="lg:col-span-2"><p className="label-xs mb-2">{t("warehouse.tr.reasonLabel")}</p>
            <NeuInput data-testid="trf-reason" value={reason} placeholder={t("warehouse.tr.reasonPlaceholder")}
                      onChange={(e) => setReason(e.target.value)} /></div>
          <div className="lg:col-span-2 flex justify-end border-t border-border/40 pt-4">
            <NeuButton variant="primary" data-testid="trf-save" disabled={!canSave} onClick={save}
                       className="bg-violet-500 text-white hover:bg-violet-600">
              <Send className="inline h-4 w-4" /> {t("warehouse.tr.notifyBoth")}</NeuButton>
          </div>
        </div>
      </div>
    </div>);
};
